/* eslint-disable no-console */
import './EventCarousel.scss'

import { ItemType, PropsType, TranslationsType } from '@react/FilterableList/FilterableListTypes'
import { usePowerPortalEvents } from '@react/FilterableList/ListType/PowerPortalEvents'
import { updateIndicators } from '@react/FilterableList/utils/event-carousel'
import { breakpointValues } from '@site/js/utils/breakpoint'
import { Options, Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide'
import { useEffect, useMemo, useRef, useState } from 'react'

import { EventCarouselArrows } from './EventCarouselComponents/EventCarouselArrows'
import { EventCarouselHeader } from './EventCarouselComponents/EventCarouselHeader'
import { EventCarouselItem } from './EventCarouselComponents/EventCarouselItem'
import { EventCarouselPagination } from './EventCarouselComponents/EventCarouselPagination'

type EventCarouselType = {
  items: ItemType[]
  eventProps: PropsType
  translations: TranslationsType
}

const splideOptions: Options = {
  type: 'loop',
  fixedWidth: 'calc(33% - var(--spacing-lg))',
  gap: 'var(--spacing-xxl)',
  width: '100%',
  padding: 'var(--spacing-lg)',
  updateOnMove: true,
  rewind: false,
  pagination: false,
  perPage: 3,
  focus: 'center',
  preloadPages: 3,
  lazyLoad: 'nearby',
  speed: 800,
  start: 0,
  drag: true,
  snap: true,
  autoInit: true,
  breakpoints: {
    [breakpointValues.downSm]: {
      fixedWidth: '100%',
      perPage: 1,
    },
  },
}

export const EventCarousel = ({ items, eventProps, translations }: EventCarouselType) => {
  // Apply PowerPortal sorting (only runs when cmp-powerportal-main is present)
  const { sortedItems } = usePowerPortalEvents({ items })
  // Use sorted items and memoize to prevent re-renders
  const itemsToRender = useMemo(() => {
    let processedItems = sortedItems

    // If .cmp-powerportal-main is present, prepend 'https://' to item.url if not already present
    if (document.querySelector('.cmp-powerportal-main')) {
      const prependHttps = url => {
        if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
          return `https://${url}`
        }
        return url
      }
      processedItems = processedItems.map(item => ({ ...item, url: item.url ? prependHttps(item.url) : item.url }))
    }

    return processedItems
  }, [sortedItems])
  const splider = useRef(null)
  const [carouselSlideItems, setCarouselSlideItems] = useState(null)
  const [currentSlideItem, setCurrentSlideItem] = useState(1)

  splider.current?.splide.on('move', () => {
    updateIndicators(splider.current.splide)
    setCurrentSlideItem(splider.current.splide.index + 1)
  })

  const handleGoToSlide = (index: number) => {
    if (splider.current) {
      splider.current.go(index)
    }
  }

  useEffect(() => {
    if (splider.current) {
      setCarouselSlideItems(splider.current.slides.filter((slide: HTMLLIElement) => !slide.classList.contains('splide__slide--clone')))
    }
  }, [items.length])

  useEffect(() => {
    if (splider.current) {
      updateIndicators(splider.current.splide)
    }
  }, [carouselSlideItems?.length])

  return (
    <>
      <EventCarouselHeader
        allEvents={translations.allEvents}
        anchorLabel={eventProps.anchorLabel}
        eventsPageLink={eventProps.eventsPageLink}
        seoHeadingElement={eventProps.seoHeadingElement}
        title={eventProps.title}
        titleId={eventProps.titleId}
      />
      <Splide ref={splider} hasTrack={false} options={splideOptions}>
        <SplideTrack>
          {itemsToRender?.map((item, index) => (
            <SplideSlide key={`${index}-${item.title}`}>
              <EventCarouselItem key={`${index}-${item.title}`} item={item} />
            </SplideSlide>
          ))}
        </SplideTrack>
        <EventCarouselArrows>
          <EventCarouselPagination
            currentSlide={currentSlideItem}
            slides={carouselSlideItems}
            goToSlide={handleGoToSlide}
            ariaLabel={translations.carouselChoseSlide}
          />
        </EventCarouselArrows>
      </Splide>
    </>
  )
}
